import { AIGeneratedField, ShortenWeekName } from '@/redux/slices/formAIGenerationSlice'
import { nanoid } from '@reduxjs/toolkit'
import dayjs from 'dayjs'
import { BuiltFieldAttributes, FieldBuilderParams } from './baseFieldBuilder'

type RawReservationItems = AIGeneratedField['reservationItems']
type RawQuotaSetting = NonNullable<RawReservationItems>[number]['quotaSetting']
type DailyTimeRangeQuota = NonNullable<NonNullable<RawQuotaSetting>['dailyTimeRangeQuotas']>[number]

type ReservationItemResult = {
  hidden?: boolean
  quotaSetting: {
    quotaExcludeDeletedEntry?: boolean
    timeRangeMode?: string
    nonReservableRules?: unknown[]
    showLeftQuota?: boolean
    type?: string
    availableDaysOfWeek?: string[]
    dailyTimeRangeQuotas?: {
      forWday?: string
      quota?: number | null
      position?: number
      startTime?: { hour: string; minute: string }
      endTime?: { hour: string; minute: string }
      dailyTimeRangeToken?: string
    }[]
    dailyTimeRanges?: {
      token: string
      name: string
      payable: boolean
      availableDaysOfWeek: string[]
      dailyTimeRangeQuotas: {
        forWday?: string
        startTime?: { hour: string; minute: string }
        endTime?: { hour: string; minute: string }
        position: number
        quota?: number | null
      }[]
    }[]
  }
  apiCode: string
  position: number
  name: string
  description?: string
  selected?: boolean
  startDate?: string
  endDate?: string
}

export default function reservationItemsBuilder({ rawField }: FieldBuilderParams): Partial<BuiltFieldAttributes> {
  return {
    reservationItems: buildReservationItems(rawField.reservationItems),
  }
}

function buildReservationItems(rawReservationItems: RawReservationItems): ReservationItemResult[] {
  let reservationItems: ReservationItemResult[] | undefined

  if (Array.isArray(rawReservationItems)) {
    reservationItems = rawReservationItems.map((item, index) => {
      const reservationItem: ReservationItemResult = {
        hidden: false,
        quotaSetting: {
          quotaExcludeDeletedEntry: true,
          timeRangeMode: 'different_by_wday',
          nonReservableRules: [],
          showLeftQuota: true,
        },
        apiCode: nanoid(),
        position: index,
        name: item.name,
        description: item.description,
        selected: false,
      }

      const startDate = item.startDate && dayjs(item.startDate)
      const endDate = item.endDate && dayjs(item.endDate)
      if (startDate) reservationItem.startDate = startDate.format()
      if (endDate) reservationItem.endDate = endDate.format()

      if (item.quotaSetting?.dailyTimeRangeQuotas?.[0]?.start) {
        setReservationItemByTimeRangeRepeatDailyQuotaSetting(item.quotaSetting, reservationItem)
      } else {
        setReservationItemByDayRepeatWeeklyQuotaSetting(item.quotaSetting, reservationItem)
      }

      return reservationItem
    })
  }

  if (!reservationItems?.length) {
    reservationItems = Array(3)
      .fill(0)
      .map((_, index) => ({
        quotaSetting: {
          type: 'by_day_repeat_weekly',
          availableDaysOfWeek: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'],
          timeRangeMode: 'different_by_wday',
        },
        apiCode: nanoid(),
        position: index,
        name: `预约项目${index + 1}`,
      }))
  }

  return reservationItems
}

const DAY_NAME_OF_WEEK_MAP: Record<ShortenWeekName, string> = {
  mon: 'monday',
  tue: 'tuesday',
  wed: 'wednesday',
  thu: 'thursday',
  fri: 'friday',
  sat: 'saturday',
  sun: 'sunday',
}

const WEEK_NAMES: Record<string, string> = {
  sunday: '日',
  monday: '一',
  tuesday: '二',
  wednesday: '三',
  thursday: '四',
  friday: '五',
  saturday: '六',
}

function setReservationItemByDayRepeatWeeklyQuotaSetting(
  rawQuotaSetting: RawQuotaSetting,
  reservationItem: ReservationItemResult
): void {
  reservationItem.quotaSetting.type = 'by_day_repeat_weekly'
  const wdays =
    rawQuotaSetting?.availableDaysOfWeek ??
    uniq(rawQuotaSetting?.dailyTimeRangeQuotas?.map(q => q.forWday).filter((w): w is ShortenWeekName => !!w))
  reservationItem.quotaSetting.availableDaysOfWeek = wdays?.map(name => DAY_NAME_OF_WEEK_MAP[name])
  reservationItem.quotaSetting.dailyTimeRangeQuotas = rawQuotaSetting?.dailyTimeRangeQuotas?.map(
    (dailyTimeRangeQuota, index) => ({
      forWday: dailyTimeRangeQuota.forWday && DAY_NAME_OF_WEEK_MAP[dailyTimeRangeQuota.forWday],
      quota: dailyTimeRangeQuota.quota,
      position: index,
    })
  )
}

type GroupedItem = { forWday: string; dailyTimeRangeQuotas: DailyTimeRangeQuota[]; group: string }

function setReservationItemByTimeRangeRepeatDailyQuotaSetting(
  rawQuotaSetting: RawQuotaSetting,
  reservationItem: ReservationItemResult
): void {
  if (!rawQuotaSetting?.dailyTimeRangeQuotas) return

  reservationItem.quotaSetting.type = 'by_time_range_repeat_daily'
  reservationItem.quotaSetting.availableDaysOfWeek = uniq(
    rawQuotaSetting.dailyTimeRangeQuotas
      .map(dailyTimeRange => dailyTimeRange.forWday)
      .filter((w): w is ShortenWeekName => !!w)
  ).map(name => DAY_NAME_OF_WEEK_MAP[name])

  const grouped = groupBy(
    rawQuotaSetting.dailyTimeRangeQuotas.filter(q => !!q.forWday),
    'forWday'
  )

  const mapped = Object.entries(grouped).map(([forWday, dailyTimeRangeQuotas]) => ({
    forWday: DAY_NAME_OF_WEEK_MAP[forWday as ShortenWeekName],
    dailyTimeRangeQuotas: sortAndFilterOverlappingDailyTimeRanges(dailyTimeRangeQuotas),
    group: dailyTimeRangeQuotas.map(quota => `${stringifyTimeRange(quota)}::${quota.quota}`).join(','),
  }))

  const regrouped = groupBy(mapped, 'group')

  reservationItem.quotaSetting.dailyTimeRanges = Object.values(regrouped).map(
    (groupedDailyTimeRangeQuotas: GroupedItem[]) => {
      const availableDaysOfWeek = groupedDailyTimeRangeQuotas.map(item => item.forWday)
      return {
        token: nanoid(),
        name: `周${availableDaysOfWeek.map(wday => WEEK_NAMES[wday]).join('、')}时段表`,
        payable: false,
        availableDaysOfWeek,
        dailyTimeRangeQuotas: groupedDailyTimeRangeQuotas[0]!.dailyTimeRangeQuotas
          .sort((a, b) => stringifyTimeRange(a).localeCompare(stringifyTimeRange(b)))
          .map((item, index) => ({
            forWday: item.forWday && DAY_NAME_OF_WEEK_MAP[item.forWday],
            startTime: parseHourMinute(item.start),
            endTime: parseHourMinute(item.end),
            position: index,
            quota: item.quota,
          })),
      }
    }
  )

  reservationItem.quotaSetting.dailyTimeRangeQuotas = reservationItem.quotaSetting.dailyTimeRanges.flatMap(
    dailyTimeRange =>
      dailyTimeRange.availableDaysOfWeek.flatMap(
        forWday =>
          dailyTimeRange.dailyTimeRangeQuotas?.map(dailyTimeRangeQuota => ({
            ...dailyTimeRangeQuota,
            forWday,
            dailyTimeRangeToken: dailyTimeRange.token,
          })) ?? []
      )
  )
}

function sortAndFilterOverlappingDailyTimeRanges(rawDailyTimeRanges: DailyTimeRangeQuota[]): DailyTimeRangeQuota[] {
  const dailyTimeRanges = rawDailyTimeRanges
    .filter(item => item.start && item.end && item.start.localeCompare(item.end) < 0)
    .map(dailyTimeRange => ({
      dailyTimeRange,
      start: hourMinuteToNumber(dailyTimeRange.start!),
      end: hourMinuteToNumber(dailyTimeRange.end!),
    }))
    .sort((a, b) => a.start - b.start)
    .reduce<{ dailyTimeRange: DailyTimeRangeQuota; start: number; end: number }[]>((acc, item) => {
      if (acc.length === 0) {
        acc.push(item)
        return acc
      }
      const lastItem = acc[acc.length - 1]
      if (lastItem && item.start >= lastItem.end) acc.push(item)
      return acc
    }, [])

  return dailyTimeRanges.map(item => item.dailyTimeRange)
}

function uniq<T>(arr: T[] | undefined): T[] {
  return Array.from(new Set(arr ?? []))
}

function groupBy<T>(arr: T[], keyOrProp: string | ((item: T) => string)): Record<string, T[]> {
  const keyFn =
    typeof keyOrProp === 'function' ? keyOrProp : (item: T) => (item as Record<string, unknown>)[keyOrProp] as string
  return arr.reduce<Record<string, T[]>>((acc, item) => {
    const key = keyFn(item)
    if (!acc[key]) acc[key] = []
    acc[key].push(item)
    return acc
  }, {})
}

function stringifyTimeRange(timeRange: { start?: string; end?: string }): string {
  return `${timeRange.start ?? ''}-${timeRange.end ?? ''}`
}

function parseHourMinute(time: string | undefined): { hour: string; minute: string } | undefined {
  if (!time) return
  const [hour = '', minute = ''] = time.split(':')
  return { hour, minute }
}

function hourMinuteToNumber(hourMinute: string): number {
  const [hour, minute] = hourMinute.split(':')
  return +(hour || 0) * 60 + +(minute || 0)
}
